import {i18n} from '@online-offer/i18n';
import {type MoneyResponseBody} from '@online-offer/shared';
import {Separator} from 'platform/components';
import {Heading, Show, VStack} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';

import {isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {Nullish} from 'shared/web';

import {useGetOnlineOfferData} from '../../hooks/useGetOnlineOfferData';
import {ReservationRow} from './ReservationRow';

/**
 * The price breakdown shared by the web and the print reservation cards. Both
 * rendered an identical copy of these rows, which is how the web card and the
 * PDF drifted apart the moment one of them gained direction-aware labels.
 */
export function ReservationPrices() {
  const {
    upsell,
    totalPriceWithVat,
    totalPriceWithoutVat,
    totalDiscountPriceWithVat,
    totalDiscountPriceWithoutVat,
    vehiclePriceWithVat,
    vehiclePriceWithoutVat,
    originalCarPriceWithVat,
    originalCarPriceWithoutVat,
    direction,
  } = useGetOnlineOfferData();

  const formatCurrency = useFormatCurrency();

  const formatPrice = (price: MoneyResponseBody | Nullish) =>
    price?.amount ? formatCurrency(parseFloat(price.amount), price.currency) : null;

  const isDiscountNonZero = (discount: MoneyResponseBody | Nullish) =>
    Number(discount?.amount ?? 0) > 0;

  // A buying offer carries a single purchase price, mapped into the vehicle
  // price slots. Only the labels differ — the layout stays identical.
  const isBuying = direction === 'BUYING';
  const carPriceLabel = isBuying
    ? i18n.t('buyingOffer.price.withVat')
    : i18n.t('reservationCard.carPrice');
  const carPriceWithoutVatLabel = isBuying
    ? i18n.t('buyingOffer.price.withoutVat')
    : i18n.t('reservationCard.carPriceWithoutVat');

  // A buying offer has no total price. Without this guard its block would
  // collapse to nothing and leave an orphan separator above the purchase price.
  const hasTotalPrice =
    isNotNil(formatPrice(totalPriceWithVat)) || isNotNil(formatPrice(totalPriceWithoutVat));

  return (
    <VStack width="100%" spacing={5}>
      <Show when={hasTotalPrice}>
        <VStack spacing={2}>
          <Show when={isNotNil(formatPrice(totalPriceWithVat))}>
            <ReservationRow
              label={i18n.t('reservationCard.totalPrice')}
              value={<Heading size={3}>{formatPrice(totalPriceWithVat)}</Heading>}
              labelProps={{alternative: true}}
            />
          </Show>
          <ReservationRow
            label={i18n.t('reservationCard.totalPriceWithoutVat')}
            value={formatPrice(totalPriceWithoutVat)}
          />
        </VStack>
        <Separator spacing={0} />
      </Show>
      <VStack spacing={2}>
        <Show when={isNotNil(formatPrice(vehiclePriceWithVat))}>
          <ReservationRow
            label={carPriceLabel}
            labelProps={{alternative: true}}
            value={<Heading size={4}>{formatPrice(vehiclePriceWithVat)}</Heading>}
          />
        </Show>
        <ReservationRow
          label={carPriceWithoutVatLabel}
          value={formatPrice(vehiclePriceWithoutVat)}
        />
        <ReservationRow
          label={i18n.t('reservationCard.originalCarPrice')}
          value={formatPrice(originalCarPriceWithVat ?? originalCarPriceWithoutVat)}
          valueProps={{color: 'danger', strikethrough: true}}
        />
        <Show
          when={
            isDiscountNonZero(totalDiscountPriceWithVat) ||
            isDiscountNonZero(totalDiscountPriceWithoutVat)
          }
        >
          <ReservationRow
            label={i18n.t('reservationCard.customerDiscount')}
            value={formatPrice(totalDiscountPriceWithVat ?? totalDiscountPriceWithoutVat)}
            valueProps={{color: 'success'}}
          />
        </Show>
      </VStack>
      <Show when={upsell && upsell.length > 0}>
        <Separator spacing={0} />
        <VStack spacing={2}>
          <ReservationRow
            label={i18n.t('reservationCard.upsell')}
            labelProps={{alternative: true}}
          />
          {upsell?.map((singleItem) => {
            if (
              isNilOrEmpty(singleItem.name) ||
              (isNilOrEmpty(singleItem.sellingPrice?.withVat) &&
                isNilOrEmpty(singleItem.sellingPrice?.withoutVat))
            ) {
              return null;
            }
            return (
              <ReservationRow
                key={singleItem.id}
                label={singleItem.name!}
                value={formatPrice(
                  singleItem.sellingPrice?.withVat ?? singleItem.sellingPrice?.withoutVat
                )}
              />
            );
          })}
        </VStack>
      </Show>
    </VStack>
  );
}
